import {ActivatedRouteSnapshot, CanActivateFn, Router} from '@angular/router';
import {inject} from '@angular/core';
import {RouteName} from '@ew/enums';
import {EW_TOASTER} from '@ew/ew-injects';
import {EwToaster, ToasterType} from '@ew/ew-services-interfaces';

/**
 * A guard function that checks for the presence of an error parameter in the current route.
 * If an error parameter is found, it redirects the user to the login page and displays an error message.
 * Otherwise, it allows the user to proceed.
 *
 * @param {ActivatedRouteSnapshot} route - The current route snapshot.
 * @return {Promise<boolean>} A promise that resolves to true if the user is allowed to proceed, otherwise false.
 */
export const authRedirectionGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
): Promise<boolean> => {
  const toaster = inject(EW_TOASTER);
  const router = inject(Router);
  const errorParam = route.queryParams['error'];

  if (errorParam) {
    // Create a new object with all queryParams except accessToken
    const queryParams = {...route.queryParams};
    delete queryParams['error'];

    router.navigate([RouteName.Login], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    // quick-fix blink mat-snack-bar
    setTimeout(() => {
      handleErrorForRedirection(errorParam, toaster);
    }, 1000);

    return false;
  }

  return true;
};

/**
 * Displays an error message using MatSnackBar based on the provided error key.
 *
 * @param {string} key - The error key to determine which message to display.
 * @param {EwToaster} toaster - The EwToaster instance used to display the message.
 */
const handleErrorForRedirection = (key: string, toaster: EwToaster): void => {
  switch (key) {
    case 'googleUserNotFound':
      toaster.displayToast('Google user not found', ToasterType.Error);
      break;
    case 'facebookUserNotFound':
      toaster.displayToast('Facebook user not found', ToasterType.Error);
      break;
    default:
      toaster.displayToast('Unknown error, please try again', ToasterType.Error);
      break;
  }
};
